import React, { useCallback } from 'react';
import { assocPath, update } from 'ramda';
import { v4 as uuid } from 'uuid';
import DeleteIcon from '@material-ui/icons/Delete';
import Fab from '@material-ui/core/Fab';
import AddIcon from '@material-ui/icons/Add';
import IconButton from '@material-ui/core/IconButton';
import Grid from '@material-ui/core/Grid';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import ListItemSecondaryAction from '@material-ui/core/ListItemSecondaryAction';
import { Select, MenuItem, Switch } from '@material-ui/core';
import { FormattedMessage } from 'react-intl';
import Typography from '@material-ui/core/Typography';

import { LayerId, LayerNodeId } from 'yeself-common/typings/guide/LayerCommon';
import { RULE_ASSIGNMENT_FORM, RULE_ASSIGNMENT_TYPE } from 'yeself-common/enum/RuleEnum';
import { RuleId, RulesDefinitionsItemAssignments } from 'yeself-common/typings/Rules';
import { FEATURE_FLAG_NAMES } from 'yeself-common/typings/FeatureFlags';
import { ContextResponse } from 'yeself-common/typings/context/Context';

import Feature from 'yeself-client-common/featureFlags/components/Feature';

import { RuleType, RuleValues } from '../types';

import RuleItem from './RuleItem';

type AssignmentsProps = {
    assignments: RulesDefinitionsItemAssignments;
    onAssignmentChange: (updatedAssignments: RulesDefinitionsItemAssignments) => void;
};

const Assignments = ({ assignments = [], onAssignmentChange }: AssignmentsProps) => {
    const isTypeEnabled = (type: string) => assignments.some((assignment) => assignment.type === type);

    const handleSwitchChange = useCallback(
        (type: string, isChecked: boolean): void => {
            const updatedAssignments: RulesDefinitionsItemAssignments = isChecked
                ? [...assignments, { type, form: RULE_ASSIGNMENT_FORM.DISABLED }]
                : assignments.filter((assignment) => assignment.type !== type);

            onAssignmentChange(updatedAssignments);
        },
        [assignments, onAssignmentChange]
    );

    const handleSelectChange = useCallback(
        (type: string, form: string): void => {
            const updatedAssignments = assignments.map((assignment) =>
                assignment.type === type ? { ...assignment, form } : assignment
            );

            onAssignmentChange(updatedAssignments);
        },
        [assignments, onAssignmentChange]
    );

    return (
        <Grid container direction="row" spacing={16}>
            <Feature name={FEATURE_FLAG_NAMES.JOURNEYS}>
                <Grid item xs={12} lg={6}>
                    <Grid container direction="row" alignItems="center" spacing={16}>
                        <div id="yeself-form-switch">
                            <Switch
                                checked={isTypeEnabled(RULE_ASSIGNMENT_TYPE.JOURNEY_TRACKER)}
                                onChange={(e) =>
                                    handleSwitchChange(
                                        RULE_ASSIGNMENT_TYPE.JOURNEY_TRACKER,
                                        e.target.checked
                                    )
                                }
                            />
                        </div>

                        <FormattedMessage
                            id={`SettingsRules.rules.assignments.type.${RULE_ASSIGNMENT_TYPE.JOURNEY_TRACKER}`}
                        />

                        <Select
                            value={
                                assignments.find((a) => a.type === RULE_ASSIGNMENT_TYPE.JOURNEY_TRACKER)
                                    ?.form || ''
                            }
                            onChange={(e) =>
                                handleSelectChange(RULE_ASSIGNMENT_TYPE.JOURNEY_TRACKER, e.target.value)
                            }
                            style={{ marginLeft: 10 }}
                            disabled={!isTypeEnabled(RULE_ASSIGNMENT_TYPE.JOURNEY_TRACKER)}
                        >
                            {Object.values(RULE_ASSIGNMENT_FORM).map((assignmentForm) => (
                                <MenuItem value={assignmentForm} key={assignmentForm}>
                                    <FormattedMessage
                                        id={`SettingsRules.rules.assignments.form.${assignmentForm}`}
                                    />
                                </MenuItem>
                            ))}
                        </Select>
                        <Typography variant="caption" color="textSecondary">
                            <FormattedMessage
                                id={`SettingsRules.rules.assignments.type.${RULE_ASSIGNMENT_TYPE.JOURNEY_TRACKER}.helperText`}
                            />
                        </Typography>
                    </Grid>
                </Grid>
            </Feature>

            <Feature name={FEATURE_FLAG_NAMES.CONTEXT_HELP}>
                <Grid item xs={12} lg={6}>
                    <Grid container direction="row" alignItems="center" spacing={16}>
                        <div id="yeself-form-switch">
                            <Switch
                                checked={isTypeEnabled(RULE_ASSIGNMENT_TYPE.CONTEXT_HELP)}
                                onChange={(e) =>
                                    handleSwitchChange(
                                        RULE_ASSIGNMENT_TYPE.CONTEXT_HELP,
                                        e.target.checked
                                    )
                                }
                            />
                        </div>

                        <FormattedMessage
                            id={`SettingsRules.rules.assignments.type.${RULE_ASSIGNMENT_TYPE.CONTEXT_HELP}`}
                        />

                        <Select
                            value={
                                assignments.find((a) => a.type === RULE_ASSIGNMENT_TYPE.CONTEXT_HELP)
                                    ?.form || ''
                            }
                            onChange={(e) =>
                                handleSelectChange(RULE_ASSIGNMENT_TYPE.CONTEXT_HELP, e.target.value)
                            }
                            style={{ marginLeft: 10 }}
                            disabled={!isTypeEnabled(RULE_ASSIGNMENT_TYPE.CONTEXT_HELP)}
                        >
                            {Object.values(RULE_ASSIGNMENT_FORM).map((assignmentForm) => (
                                <MenuItem value={assignmentForm} key={assignmentForm}>
                                    <FormattedMessage
                                        id={`SettingsRules.rules.assignments.form.${assignmentForm}`}
                                    />
                                </MenuItem>
                            ))}
                        </Select>
                        <Typography variant="caption" color="textSecondary">
                            <FormattedMessage
                                id={`SettingsRules.rules.assignments.type.${RULE_ASSIGNMENT_TYPE.CONTEXT_HELP}.helperText`}
                            />
                        </Typography>
                    </Grid>
                </Grid>
            </Feature>
        </Grid>
    );
};

export type RulesOptions = {
    ruleType: RuleType[];
    guideNames: {
        value: LayerId;
        label: string;
    }[];
    attributeNames: {
        value: string;
        label: string;
    }[];
    interactionCollectorNodeNames: {
        value: LayerId;
        label: string;
        options: {
            value: LayerNodeId;
            label: string;
        }[];
    }[];
    contextResponse: ContextResponse;
};

export type RulesProps = {
    value: RuleValues[];
    onChange: (value: RuleValues[]) => void;
    options: RulesOptions;
    assignments?: {
        enabled: boolean;
        defaultValue: RulesDefinitionsItemAssignments;
    };
};

const Rules = ({
    value = [],
    onChange,
    options,
    assignments = {
        enabled: false,
        defaultValue: [],
    },
}: RulesProps): React.ReactElement => {
    const handleRuleDefinitionOnDelete = useCallback(
        (indexToDelete: number) => () => {
            onChange(value.filter((_, index) => indexToDelete !== index));
        },
        [value, onChange]
    );

    const handleRuleDefinitionOnAdd = useCallback(() => {
        onChange([
            ...value,
            {
                id: uuid() as RuleId,
                type: '',
                level: [],
                assignments: assignments.defaultValue,
            },
        ]);
    }, [onChange, value, assignments.defaultValue]);

    const handleRuleOnChange = useCallback(
        (index: number) => (newValue: RuleValues) => {
            onChange(update(index, newValue, value));
        },
        [value, onChange]
    );

    const handleAssignmentOnChange = useCallback(
        (index: number) => (updatedAssignments: RulesDefinitionsItemAssignments) => {
            onChange(update(index, assocPath(['assignments'], updatedAssignments, value[index]), value));
        },
        [value, onChange]
    );

    return (
        <Grid container alignItems="center">
            {value.length > 0 && (
                <Grid item xs={12}>
                    <List>
                        {value.map((val, index) => (
                            <ListItem key={val.id} style={{}}>
                                <ListItemText>
                                    <RuleItem
                                        value={val}
                                        options={options}
                                        onChange={handleRuleOnChange(index)}
                                    />

                                    {assignments.enabled && (
                                        <ListItem divider={value.length !== index + 1} disableGutters>
                                            <ListItemText>
                                                <Assignments
                                                    assignments={val.assignments}
                                                    onAssignmentChange={handleAssignmentOnChange(index)}
                                                />
                                            </ListItemText>
                                        </ListItem>
                                    )}
                                </ListItemText>

                                <ListItemSecondaryAction>
                                    <IconButton onClick={handleRuleDefinitionOnDelete(index)}>
                                        <DeleteIcon />
                                    </IconButton>
                                </ListItemSecondaryAction>
                            </ListItem>
                        ))}
                    </List>
                </Grid>
            )}

            <Grid item xs={12}>
                <div id="yeself-decision-add-button" style={{ padding: 8 }}>
                    <Grid container spacing={16} justify="flex-end">
                        <Grid item>
                            <Fab size="small" color="primary" onClick={handleRuleDefinitionOnAdd}>
                                <AddIcon />
                            </Fab>
                        </Grid>
                    </Grid>
                </div>
            </Grid>
        </Grid>
    );
};

export default Rules;
